/**
 * Configuration Service
 * Centralizes all configuration values and removes hardcoded fallback data
 */

import { log } from "../config/env";
import { PASSWORD_MIN_LENGTH } from "../utils/validation";

// Safe wrapper for env access in both Node (tests) and browser:
// - In Node, `process.env` is available as usual.
// - In the browser, `process` may be undefined until shims run, so we guard on typeof.
// This prevents runtime "process is not defined" errors when this module is imported early.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const env: Record<string, any> =
  typeof process !== "undefined" && (process as any).env
    ? ((process as any).env as Record<string, any>)
    : {};

export interface AppConfig {
  api: {
    timeout: number;
    retryAttempts: number;
    retryDelay: number;
  };
  websocket: {
    reconnectInterval: number;
    maxReconnectAttempts: number;
    heartbeatInterval: number;
  };
  geocoding: {
    timeout: number;
    cacheSize: number;
    retryAttempts: number;
  };
  auth: {
    tokenRefreshThreshold: number;
    sessionTimeout: number;
    maxLoginAttempts: number;
  };
  ui: {
    debounceDelay: number;
    animationDuration: number;
    maxSearchResults: number;
  };
  security: {
    maxFileSize: number;
    allowedFileTypes: string[];
    passwordMinLength: number;
    sessionMaxAge: number;
  };
  performance: {
    maxLogHistory: number;
    memoryLeakCheckInterval: number;
    cacheExpirationTime: number;
  };
}

class ConfigurationService {
  private static instance: ConfigurationService | null = null;
  private config: AppConfig;

  private constructor() {
    this.config = this.loadConfiguration();
  }

  static getInstance(): ConfigurationService {
    if (!ConfigurationService.instance) {
      ConfigurationService.instance = new ConfigurationService();
    }
    return ConfigurationService.instance;
  }

  /**
   * Load configuration from environment variables and defaults
   */
  private loadConfiguration(): AppConfig {
    return {
      api: {
        timeout: Number(env.VITE_API_TIMEOUT) || 30000,
        retryAttempts: Number(env.VITE_API_RETRY_ATTEMPTS) || 3,
        retryDelay: Number(env.VITE_API_RETRY_DELAY) || 1000,
      },
      websocket: {
        reconnectInterval:
          Number(env.VITE_WS_RECONNECT_INTERVAL) || 5000,
        maxReconnectAttempts:
          Number(env.VITE_WS_MAX_RECONNECT_ATTEMPTS) || 5,
        heartbeatInterval:
          Number(env.VITE_WS_HEARTBEAT_INTERVAL) || 30000,
      },
      geocoding: {
        timeout: Number(env.VITE_GEOCODING_TIMEOUT) || 5000,
        cacheSize: Number(env.VITE_GEOCODING_CACHE_SIZE) || 1000,
        retryAttempts: Number(env.VITE_GEOCODING_RETRY_ATTEMPTS) || 2,
      },
      auth: {
        tokenRefreshThreshold:
          Number(env.VITE_AUTH_TOKEN_REFRESH_THRESHOLD) || 300000, // 5 minutes
        sessionTimeout:
          Number(env.VITE_AUTH_SESSION_TIMEOUT) || 3600000, // 1 hour
        maxLoginAttempts: Number(env.VITE_AUTH_MAX_LOGIN_ATTEMPTS) || 5,
      },
      ui: {
        debounceDelay: Number(env.VITE_UI_DEBOUNCE_DELAY) || 300,
        animationDuration:
          Number(env.VITE_UI_ANIMATION_DURATION) || 200,
        maxSearchResults: Number(env.VITE_UI_MAX_SEARCH_RESULTS) || 50,
      },
      security: {
        maxFileSize:
          Number(env.VITE_SECURITY_MAX_FILE_SIZE) || 10485760, // 10MB
        allowedFileTypes: (
          env.VITE_SECURITY_ALLOWED_FILE_TYPES ||
          "jpg,jpeg,png,pdf,doc,docx"
        ).split(","),
        passwordMinLength: PASSWORD_MIN_LENGTH,
        sessionMaxAge:
          Number(env.VITE_SECURITY_SESSION_MAX_AGE) || 86400000, // 24 hours
      },
      performance: {
        maxLogHistory:
          Number(env.VITE_PERFORMANCE_MAX_LOG_HISTORY) || 1000,
        memoryLeakCheckInterval:
          Number(env.VITE_PERFORMANCE_MEMORY_LEAK_CHECK_INTERVAL) || 60000, // 1 minute
        cacheExpirationTime:
          Number(env.VITE_PERFORMANCE_CACHE_EXPIRATION_TIME) || 300000, // 5 minutes
      },
    };
  }

  /**
   * Get configuration value by path
   */
  get<T = unknown>(path: string): T {
    const keys = path.split(".");
    let value: unknown = this.config;

    for (const key of keys) {
      if (typeof value === "object" && value !== null && key in value) {
        value = (value as Record<string, unknown>)[key];
      } else {
        log("warn", `Configuration path not found: ${path}`, "PERFORMANCE");
        return undefined as T;
      }
    }

    return value as T;
  }

  /**
   * Get API configuration
   */
  getApiConfig() {
    return this.config.api;
  }

  /**
   * Get WebSocket configuration
   */
  getWebSocketConfig() {
    return this.config.websocket;
  }

  /**
   * Get geocoding configuration
   */
  getGeocodingConfig() {
    return this.config.geocoding;
  }

  /**
   * Get authentication configuration
   */
  getAuthConfig() {
    return this.config.auth;
  }

  /**
   * Get UI configuration
   */
  getUIConfig() {
    return this.config.ui;
  }

  /**
   * Get security configuration
   */
  getSecurityConfig() {
    return this.config.security;
  }

  /**
   * Get performance configuration
   */
  getPerformanceConfig() {
    return this.config.performance;
  }

  /**
   * Update configuration at runtime
   */
  updateConfig(path: string, value: unknown): void {
    const keys = path.split(".");
    let target: Record<string, unknown> = this.config as unknown as Record<
      string,
      unknown
    >;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in target) || typeof target[key] !== "object") {
        target[key] = {};
      }
      target = target[key] as Record<string, unknown>;
    }

    target[keys[keys.length - 1]] = value;
    log("info", `Configuration updated: ${path}`, "PERFORMANCE", { value });
  }

  /**
   * Reset configuration to defaults
   */
  resetConfig(): void {
    this.config = this.loadConfiguration();
    log("info", "Configuration reset to defaults", "PERFORMANCE");
  }

  /**
   * Export current configuration
   */
  exportConfig(): string {
    return JSON.stringify(this.config, null, 2);
  }

  /**
   * Get all configuration
   */
  getAllConfig(): AppConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const config = ConfigurationService.getInstance();

// Export convenience functions
export const getConfig = <T = unknown>(path: string): T => config.get<T>(path);
export const getApiConfig = () => config.getApiConfig();
export const getWebSocketConfig = () => config.getWebSocketConfig();
export const getGeocodingConfig = () => config.getGeocodingConfig();
export const getAuthConfig = () => config.getAuthConfig();
export const getUIConfig = () => config.getUIConfig();
export const getSecurityConfig = () => config.getSecurityConfig();
export const getPerformanceConfig = () => config.getPerformanceConfig();
export const updateConfig = (path: string, value: unknown) =>
  config.updateConfig(path, value);
export const resetConfig = () => config.resetConfig();
export const exportConfig = () => config.exportConfig();
